import { useEffect, useState } from "react";
import { Toast as ToastType } from "@/shared/contexts/ToastContext";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, AlertTriangle, Check, Info } from "lucide-react";

interface ToastProps {
  toast: ToastType;
  onRemove: () => void;
}

// How long the fade-out animation runs before the toast is removed from the DOM.
const EXIT_ANIMATION_MS = 300;

const Toast = ({ toast, onRemove }: ToastProps) => {
  const [visible, setVisible] = useState(true);
  const toastDuration = toast.duration;

  // Auto-dismiss: trigger the fade-out shortly before the toast is removed, so
  // the exit animation has time to play. No progress bar — the toast simply
  // fades away (the countdown bar was confusing users).
  useEffect(() => {
    const visibleDuration = Math.max(0, toastDuration - EXIT_ANIMATION_MS);
    const timer = setTimeout(() => setVisible(false), visibleDuration);
    return () => clearTimeout(timer);
  }, [toastDuration]);

  // Once the fade-out animation completes, remove the toast from the list.
  useEffect(() => {
    if (visible) return undefined;
    const timer = setTimeout(onRemove, EXIT_ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [visible, onRemove]);

  // Get the appropriate icon and background color based on toast type
  const getToastStyles = () => {
    switch (toast.type) {
      case "success":
        return {
          icon: <Check className="w-5 h-5" />,
          bgColor: "bg-accent-success text-text-inverse",
        };
      case "error":
        return {
          icon: <AlertCircle className="w-5 h-5" />,
          bgColor: "bg-accent-error text-text-inverse",
        };
      case "warning":
        return {
          icon: <AlertTriangle className="w-5 h-5" />,
          bgColor: "bg-accent-warning text-text-inverse",
        };
      case "info":
      default:
        return {
          icon: <Info className="w-5 h-5" />,
          bgColor: "bg-accent-info text-text-inverse",
        };
    }
  };

  const { icon, bgColor } = getToastStyles();

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 15, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: EXIT_ANIMATION_MS / 1000, ease: "easeOut" }}
          className={`${bgColor} rounded-lg shadow-lg flex w-full max-w-md mx-auto overflow-hidden md:mx-0`}
          style={{
            minWidth: "280px",
            maxWidth: "100%",
          }}
          role="alert"
        >
          <div className="p-3 md:p-3 flex items-center gap-2 md:gap-3">
            <div className="shrink-0">{icon}</div>
            <p className="text-sm md:text-sm font-medium flex-1 wrap-break-words">
              {toast.message}
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Toast;
